// paex-announcement-bar.js — Announcement bar - PE (Halloween clock - PE, 2026-10-09).
// Loaded (defer) by sections/paex-announcement-bar.liquid only when the bar has
// 2+ slides or a Halloween deadline slide. No libraries. Two parts:
//
// 1. <paex-ab-rotator> — replaces the native <slider-component> rotation.
//    One slide shown at a time (the others carry [hidden]); block order =
//    rotation order. Autoplay only with data-autoplay (auto-rotate on, 2+
//    slides). Modelled on <paex-cpb-rotator> (assets/paex-clickable-promo.js):
//    a light heartbeat counts UNBLOCKED time and switches after a full
//    interval; any hold resets the count. Holds: hover (mouse/pen), touch,
//    keyboard focus inside (pause button excluded — APG carousel), user
//    (pause pressed, or prefers-reduced-motion: never autoplays), hidden tab,
//    offscreen, editor (a block selected in the theme editor), sheet (the
//    postal sheet is open: paex:postal-open / paex:postal-close).
//    aria-live is "off" by default (no announcement for autoplay or clock
//    repaints); "polite" only around a prev/next press, then back to "off".
//    Prev/next arrows: same native buttons (shown on hover by
//    paex-global.css via .slider--installed; hidden on phones with the clock);
//    [data-still] (paused / reduced motion / no auto-rotate) and keyboard
//    focus inside keep them shown at every width (paex-announcement-bar.css).
//
// 2. Halloween deadline slide ([data-paex-adl]) — follows window.paexClock
//    (assets/paex-clock.js, loaded later at the end of body):
//      pending            → keep the server text (no range, no date)
//      unknown, no place  → days · range + "Get your date" (range only while
//                           every region still makes it; days only when the
//                           slowest misses; late wording when all miss)
//      unknown, a place   → days + "Change" (remote / US / no transit row:
//                           no range, no date — never a promise we can't keep)
//      ontime / lastday   → days · "Get it {date}" + "Change"
//      late               → late wording + stores link
//      eve                → eve text only
//      day                → day text + stores link
//      off                → the slide is removed from the rotation (a page
//                           cached past Halloween never rotates a blank slide)
//    Text is built with textContent only (no innerHTML with data). The box
//    height never changes: the section reserves it (paex-announcement-bar.css).
(function () {
  'use strict';

  var BEAT_MS = 250;
  var LIVE_MS = 1500; // aria-live stays "polite" this long after a prev/next press
  var MIN_S = 3;
  var MAX_S = 20;

  /* ---------------- deadline slide ---------------- */

  // Build "template with {tokens}" into el; tokens listed in marks become
  // <b>/<span> elements with the given class, the rest plain text.
  function rich(el, template, vars, marks) {
    var parts = String(template || '').split(/\{(\w+)\}/);
    parts.forEach(function (part, i) {
      if (i % 2 === 0) {
        if (part) el.appendChild(document.createTextNode(part));
        return;
      }
      var value = vars && vars[part] != null ? String(vars[part]) : '{' + part + '}';
      var mark = marks && marks[part];
      if (mark) {
        var node = document.createElement(mark.tag || 'span');
        node.className = mark.cls;
        node.textContent = value;
        el.appendChild(node);
      } else {
        el.appendChild(document.createTextNode(value));
      }
    });
  }

  // "Halloween in {days} days" with the number in brand orange
  function daysPhrase(clock, days) {
    var b = document.createElement('b');
    b.className = 'paex-adl__n';
    rich(b, (clock.config.texts || {}).days || '', { days: days }, { days: { cls: 'paex-adl__num' } });
    return b;
  }

  function mode(state) {
    if (state.phase === 'eve') return 'eve';
    if (state.phase === 'day') return 'day';
    if (state.verdict === 'late') return 'late';
    if ((state.verdict === 'ontime' || state.verdict === 'lastday') && state.dateLabel) return 'known';
    var range = state.range;
    if (range && range.allLate) return 'late';
    var placed = state.destination && state.destination.source !== 'none';
    if (placed) return 'placed';
    if (range && !range.someLate) return 'range';
    return 'days';
  }

  function paintMsg(el, clock, state, kind, size) {
    var T = clock.config.texts || {};
    el.textContent = '';
    if (kind === 'eve') { el.textContent = T.eve || ''; return; }
    if (kind === 'day') { el.textContent = T.day || ''; return; }
    if (kind === 'late') { el.textContent = T['bar_late_' + size] || ''; return; }
    el.appendChild(daysPhrase(clock, state.days));
    if (kind === 'range') {
      el.appendChild(document.createTextNode(' · '));
      rich(el, T['bar_unknown_' + size], { min: state.range.min, max: state.range.max });
    } else if (kind === 'known') {
      el.appendChild(document.createTextNode(' · '));
      rich(el, T['bar_known_' + size], { date: state.dateLabel }, { date: { tag: 'b', cls: 'paex-adl__date' } });
    }
  }

  function paintSlide(slide, clock, state) {
    if (!state) return;
    if (state.phase === 'off') {
      dropSlide(slide);
      return;
    }
    if (state.pending) return;
    var T = clock.config.texts || {};
    var kind = mode(state);
    slide.setAttribute('data-adl-state', kind);
    ['s', 'l'].forEach(function (size) {
      var msg = slide.querySelector('[data-adl-msg="' + size + '"]');
      if (msg) paintMsg(msg, clock, state, kind, size);
    });
    // date / change button (opens the postal sheet)
    var dateBtn = slide.querySelector('[data-adl-date]');
    if (dateBtn) {
      var change = kind === 'known' || kind === 'placed';
      dateBtn.hidden = !(kind === 'range' || kind === 'days' || change);
      ['s', 'l'].forEach(function (size) {
        var label = dateBtn.querySelector('[data-adl-label="' + size + '"]');
        if (label) label.textContent = change ? (T.link_change || '') : (T['link_date_' + size] || '');
      });
    }
    // stores link (block URL, else the theme setting; hidden when both empty)
    var stores = slide.querySelector('[data-adl-stores]');
    if (stores) {
      var url = slide.getAttribute('data-stores-url') || clock.config.storesUrl || '';
      if (url) stores.setAttribute('href', url);
      stores.hidden = !url || !(kind === 'late' || kind === 'day');
    }
  }

  function dropSlide(slide) {
    var rotator = slide.closest('paex-ab-rotator');
    if (rotator && typeof rotator.drop === 'function') rotator.drop(slide);
    else slide.remove();
  }

  function paintAll(root) {
    var clock = window.paexClock;
    if (!clock) return;
    var state = clock.state;
    (root || document).querySelectorAll('[data-paex-adl]').forEach(function (slide) {
      paintSlide(slide, clock, state);
    });
  }

  /* ---------------- rotator ---------------- */

  // Defined BEFORE the clock wiring below: an 'off' state painted at load
  // (clock already ready, e.g. theme editor) must go through rotator.drop()
  // so a bar left with no slide hides instead of showing an empty band.
  if (!customElements.get('paex-ab-rotator')) customElements.define(
    'paex-ab-rotator',
    class PaexAbRotator extends HTMLElement {
      connectedCallback() {
        if (this.started) return;
        this.started = true;
        this.track = this.querySelector('[data-ab-track]');
        this.toggle = this.querySelector('[data-ab-toggle]');
        this.prev = this.querySelector('[data-ab-prev]');
        this.next = this.querySelector('[data-ab-next]');
        this.collect();
        if (!this.slides.length) {
          // every slide already removed (clock off on a cached page): no blank band
          var section = this.closest('.shopify-section') || this.closest('[data-paex-ab]');
          if (section) section.hidden = true;
          return;
        }
        this.index = Math.max(0, this.slides.findIndex(function (el) { return !el.hidden; }));
        this.holds = {};
        this.beat = null;
        this.elapsed = 0;
        this.onBeat = this.beatTick.bind(this);
        this.on = [];

        var seconds = parseInt(this.getAttribute('data-interval'), 10);
        if (!(seconds >= MIN_S)) seconds = 5;
        this.intervalMs = Math.min(MAX_S, seconds) * 1000;
        this.autoplay = this.hasAttribute('data-autoplay');

        // the clock may already be ready (theme editor section reload)
        paintAll(this);

        if (this.prev) this.listen(this.prev, 'click', this.step.bind(this, -1));
        if (this.next) this.listen(this.next, 'click', this.step.bind(this, 1));
        if (this.slides.length > 1) this.classList.add('slider--installed');

        this.listen(document, 'shopify:block:select', this.onEditorSelect.bind(this));
        this.listen(document, 'shopify:block:deselect', this.onEditorDeselect.bind(this));
        this.listen(document, 'paex:postal-open', this.onSheetOpen.bind(this));
        this.listen(document, 'paex:postal-close', this.onSheetClose.bind(this));

        if (!this.autoplay || this.slides.length < 2) {
          this.syncControls();
          return;
        }

        this.listen(this, 'pointerenter', this.onPointerEnter.bind(this));
        this.listen(this, 'pointerleave', this.onPointerLeave.bind(this));
        this.listen(this, 'pointerdown', this.onPointerDown.bind(this));
        this.listen(this, 'pointerup', this.onPointerUp.bind(this));
        this.listen(this, 'pointercancel', this.onPointerUp.bind(this));
        this.listen(this, 'focusin', this.onFocusIn.bind(this));
        this.listen(this, 'focusout', this.onFocusOut.bind(this));
        this.listen(document, 'visibilitychange', this.onVisibility.bind(this));
        if (this.toggle) this.listen(this.toggle, 'click', this.onToggle.bind(this));

        this.motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
        if (this.motion) {
          this.onMotion = this.onMotionChange.bind(this);
          if (this.motion.addEventListener) this.motion.addEventListener('change', this.onMotion);
          else if (this.motion.addListener) this.motion.addListener(this.onMotion);
          if (this.motion.matches) this.holds.user = true;
        }

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            function (entries) {
              var entry = entries[entries.length - 1];
              this.setHold('offscreen', !entry.isIntersecting);
            }.bind(this)
          );
          this.observer.observe(this);
        }
        if (document.hidden) this.holds.hidden = true;

        this.syncControls();
        this.restart();
      }

      disconnectedCallback() {
        this.stop();
        if (this.liveTimer) clearTimeout(this.liveTimer);
        this.liveTimer = null;
        (this.on || []).forEach(function (l) {
          l[0].removeEventListener(l[1], l[2]);
        });
        this.on = [];
        if (this.observer) this.observer.disconnect();
        if (this.motion && this.onMotion) {
          if (this.motion.removeEventListener) this.motion.removeEventListener('change', this.onMotion);
          else if (this.motion.removeListener) this.motion.removeListener(this.onMotion);
        }
        this.started = false;
      }

      listen(target, type, handler) {
        target.addEventListener(type, handler);
        this.on.push([target, type, handler]);
      }

      collect() {
        this.slides = this.track
          ? Array.prototype.slice.call(this.track.children).filter(function (el) {
            return el.hasAttribute('data-ab-slide');
          })
          : [];
      }

      rotating() {
        return this.autoplay && this.slides.length > 1;
      }

      // ---- clock ---------------------------------------------------------------
      held() {
        for (var key in this.holds) {
          if (this.holds[key]) return true;
        }
        return false;
      }

      setHold(name, on) {
        var was = !!this.holds[name];
        this.holds[name] = !!on;
        if (was === !!on) return;
        this.restart(); // stops while held; a FULL interval once the last hold clears
      }

      restart() {
        this.elapsed = 0;
        if (!this.rotating() || this.held()) {
          this.stop();
          return;
        }
        if (!this.beat) {
          this.last = Date.now();
          this.beat = setInterval(this.onBeat, BEAT_MS);
        }
      }

      stop() {
        if (this.beat) clearInterval(this.beat);
        this.beat = null;
      }

      beatTick() {
        var now = Date.now();
        var delta = Math.min(now - this.last, BEAT_MS * 4); // throttled-timer guard
        this.last = now;
        if (this.held()) return;
        this.elapsed += delta;
        if (this.elapsed >= this.intervalMs) {
          this.elapsed = 0;
          this.show((this.index + 1) % this.slides.length, true);
        }
      }

      show(nextIndex, animate) {
        if (!this.slides.length) return;
        var outgoing = this.slides[this.index];
        var focused = document.activeElement;
        var moveFocus = !!(outgoing && focused && focused !== document.body && outgoing.contains(focused));
        var reduce = this.motion ? this.motion.matches : false;
        this.slides.forEach(function (slide, i) {
          var active = i === nextIndex;
          slide.hidden = !active;
          slide.classList.toggle('is-entering', !!(active && animate && !reduce));
        });
        this.index = nextIndex;
        // never strand focus inside a slide that just went hidden
        if (moveFocus) {
          var target = this.slides[nextIndex].querySelector('a[href], button:not([hidden])') || this.toggle || this.next;
          if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
        }
      }

      step(direction, event) {
        if (event) event.preventDefault();
        if (this.slides.length < 2) return;
        // user-initiated: announce the slide it brings in, then back to "off"
        this.announce(true);
        this.show((this.index + direction + this.slides.length) % this.slides.length, true);
        this.elapsed = 0;
        this.liveTimer = setTimeout(this.announce.bind(this, false), LIVE_MS);
      }

      // aria-live: "polite" only around a user step (set BEFORE the change so
      // screen readers pick it up), "off" otherwise
      announce(on) {
        if (this.liveTimer) clearTimeout(this.liveTimer);
        this.liveTimer = null;
        if (this.track) this.track.setAttribute('aria-live', on ? 'polite' : 'off');
      }

      // Remove one slide (deadline slide once the occasion is over). With no
      // slide left the whole section hides: nothing blank ever rotates.
      drop(slide) {
        var at = this.slides.indexOf(slide);
        slide.remove();
        this.collect();
        if (!this.slides.length) {
          this.stop();
          var section = this.closest('.shopify-section') || this.closest('[data-paex-ab]');
          if (section) section.hidden = true;
          return;
        }
        // a slide before the active one shifts it down; the active one hands
        // over to the slide that followed it
        if (at !== -1 && at < this.index) this.index -= 1;
        if (this.index >= this.slides.length) this.index = 0;
        this.show(this.index, false);
        if (this.slides.length < 2) {
          this.classList.remove('slider--installed');
          if (this.prev) this.prev.hidden = true;
          if (this.next) this.next.hidden = true;
        }
        this.syncControls();
        this.restart();
      }

      // ---- events --------------------------------------------------------------
      onPointerEnter(event) {
        if (event.pointerType === 'touch') return;
        this.setHold('hover', true);
      }

      onPointerLeave(event) {
        if (event.pointerType === 'touch') return;
        this.setHold('hover', false);
      }

      onPointerDown(event) {
        if (event.pointerType !== 'touch') return;
        if (this.toggle && this.toggle.contains(event.target)) return;
        this.setHold('touch', true);
      }

      onPointerUp(event) {
        if (event.pointerType !== 'touch') return;
        this.setHold('touch', false);
      }

      onFocusIn(event) {
        if (this.toggle && this.toggle.contains(event.target)) return;
        // keyboard focus only — a lingering click focus must not freeze the
        // rotation for good (hover/touch cover pointer users)
        var keyboard = true;
        try {
          keyboard = event.target.matches(':focus-visible');
        } catch (error) {
          /* no :focus-visible support — treat every focus as keyboard focus */
        }
        if (keyboard) this.setHold('focus', true);
      }

      onFocusOut(event) {
        var to = event.relatedTarget;
        if (to && this.contains(to) && !(this.toggle && this.toggle.contains(to))) return;
        this.setHold('focus', false);
      }

      onVisibility() {
        this.setHold('hidden', document.hidden);
      }

      onMotionChange() {
        if (this.motion && this.motion.matches) {
          this.holds.user = true;
          this.restart();
          this.syncControls();
        }
      }

      onToggle(event) {
        event.preventDefault();
        if (this.holds.user) {
          // explicit Play: drop the interaction holds the shopper sits behind
          this.holds.hover = false;
          this.holds.focus = false;
          this.holds.touch = false;
          this.holds.user = false;
        } else {
          this.holds.user = true;
        }
        this.syncControls();
        this.restart();
      }

      // Postal sheet (assets/paex-clock.js): no slide change while it is open.
      // The close event fires BEFORE focus goes back to the trigger, so a
      // trigger left in a hidden slide is shown first.
      onSheetOpen(event) {
        var detail = event && event.detail;
        this.sheetTrigger = detail && detail.trigger ? detail.trigger : null;
        this.setHold('sheet', true);
      }

      onSheetClose() {
        var trigger = this.sheetTrigger;
        this.sheetTrigger = null;
        if (trigger && trigger.nodeType === 1 && this.contains(trigger)) {
          var at = this.slides.findIndex(function (slide) { return slide.contains(trigger); });
          if (at !== -1 && this.slides[at].hidden) this.show(at, false);
        }
        this.setHold('sheet', false);
      }

      onEditorSelect(event) {
        var at = this.slides.indexOf(event.target);
        if (at === -1) return;
        this.show(at, false);
        this.setHold('editor', true);
      }

      onEditorDeselect(event) {
        if (this.slides.indexOf(event.target) === -1) return;
        this.setHold('editor', false);
      }

      // Toggle: visible only while the bar rotates. The label stays "Pause
      // announcements" and aria-pressed carries the state (WAI-ARIA toggle
      // button: a pressed button must not also change its name); the title
      // tells sighted mouse users which action a click performs.
      syncControls() {
        var rotating = this.rotating();
        var paused = !!this.holds.user;
        // standing still for good (paused / reduced motion / no rotation):
        // arrows stay shown at every width so every slide stays reachable
        this.toggleAttribute('data-still', !rotating || paused);
        if (this.toggle) {
          this.toggle.classList.remove('is-pending');
          this.toggle.hidden = !rotating;
          this.toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
          this.toggle.setAttribute('data-state', paused ? 'paused' : 'playing');
          var pause = this.getAttribute('data-label-pause');
          var play = this.getAttribute('data-label-play');
          if (pause) this.toggle.setAttribute('aria-label', pause);
          var hint = paused ? play : pause;
          if (hint) this.toggle.setAttribute('title', hint);
        }
      }
    }
  );

  /* ---------------- Free shipping offer - PE slide (2026-10-19, design A1) ----------------
     [data-paex-fso-slide] follows window.paexShippingOffer (assets/paex-shipping-offer.js,
     loaded only while the master switch is on): the zone TEASER (metro "Free shipping over
     99 $ in most areas*", regional "{value} off shipping over {threshold} in your region*");
     a remote destination has no offer, so its slide is dropped like an 'off' clock slide. */
  function paintOffers(root) {
    var engine = window.paexShippingOffer;
    if (!engine || !engine.state) return;
    var state = engine.state;
    (root || document).querySelectorAll('[data-paex-fso-slide]').forEach(function (slide) {
      if (state.zone === 'remote') { dropSlide(slide); return; }
      var msg = slide.querySelector('[data-fso-slide-msg]');
      if (!msg || !state.teaser) return;
      var textNode = null;
      for (var i = 0; i < msg.childNodes.length; i += 1) {
        if (msg.childNodes[i].nodeType === 3) { textNode = msg.childNodes[i]; break; }
      }
      if (!textNode) { textNode = document.createTextNode(''); msg.insertBefore(textNode, msg.firstChild); }
      if (textNode.nodeValue !== state.teaser) textNode.nodeValue = state.teaser;
    });
  }
  if (!window.paexAbOfferWired) {
    window.paexAbOfferWired = true;
    document.addEventListener('paex:offer-change', function () { paintOffers(document); });
    if (window.paexShippingOffer) paintOffers(document);
  }

  /* ---------------- clock wiring (one document listener per page) ---------------- */

  if (!window.paexAbClockWired) {
    window.paexAbClockWired = true;
    var startClock = function () {
      paintAll(document);
      document.addEventListener('paex:clock-change', function () { paintAll(document); });
    };
    if (window.paexClock) startClock();
    else document.addEventListener('paex:clock-ready', startClock, { once: true });
  }
})();
